const DEFAULT_FALLBACK_MESSAGE =
  "Something went wrong. Please try again later.";

const ACTIONABLE_PATTERNS: RegExp[] = [
  /invalid email or password/i,
  /account is locked/i,
  /password must be at least/i,
  /passwords do not match/i,
  /email already (exists|registered)/i,
  /please select/i,
  /please enter/i,
  /please provide/i,
  /no speech (was )?detected/i,
  /microphone/i,
  /not (found|available)/i,
  /must be logged in/i,
];

const TECHNICAL_PATTERNS: RegExp[] = [
  /json/i,
  /unexpected token/i,
  /syntaxerror/i,
  /typeerror/i,
  /referenceerror/i,
  /traceback/i,
  /stack/i,
  /request failed with status/i,
  /http\s*\d{3}/i,
  /failed to fetch/i,
  /networkerror/i,
  /cors/i,
];

function toMessage(value: unknown): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (value instanceof Error) {
    return value.message.trim();
  }
  if (typeof value === "object") {
    const maybeMessage = (value as { message?: unknown }).message;
    if (typeof maybeMessage === "string") {
      return maybeMessage.trim();
    }
  }
  return "";
}

function isLikelyActionable(message: string): boolean {
  return ACTIONABLE_PATTERNS.some((pattern) => pattern.test(message));
}

function isLikelyTechnical(message: string): boolean {
  return TECHNICAL_PATTERNS.some((pattern) => pattern.test(message));
}

export function getUserErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_FALLBACK_MESSAGE
): string {
  const message = toMessage(error);
  if (!message) {
    return fallback;
  }

  if (isLikelyActionable(message) && !isLikelyTechnical(message)) {
    return message;
  }

  return fallback;
}

export function getApiStatusMessage(status: number): string {
  if (status === 400 || status === 422) {
    return "Please check your input and try again.";
  }
  if (status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  if (status === 403) {
    return "You don't have permission to do that.";
  }
  if (status === 404) {
    return "We couldn't find what you requested.";
  }
  if (status === 409) {
    return "That action couldn't be completed right now.";
  }
  if (status === 429) {
    return "Too many requests. Please wait a moment and try again.";
  }
  if (status >= 500) {
    return "Something went wrong on our side. Please try again later.";
  }
  return DEFAULT_FALLBACK_MESSAGE;
}

export const GENERIC_ERROR_MESSAGE = DEFAULT_FALLBACK_MESSAGE;
